import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiClock } from 'react-icons/fi';
import { Container, Section, PageHero, HeroArt, Reveal, StatusPill } from '../components/ui';
import { KEY_DATES, CONFERENCE } from '../data/conference';

/* orbital clock motif — copper dial with a sweeping hand */
const DatesArt = (
  <HeroArt viewBox="0 0 300 260" aria-hidden="true">
    <circle className="frame faint" cx="150" cy="130" r="118" />
    <circle className="frame soft" cx="150" cy="130" r="92" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * 30 * Math.PI) / 180;
      const r1 = 92;
      const r2 = i % 3 === 0 ? 76 : 84;
      return (
        <line
          key={i}
          className="frame"
          x1={150 + r1 * Math.sin(a)}
          y1={130 - r1 * Math.cos(a)}
          x2={150 + r2 * Math.sin(a)}
          y2={130 - r2 * Math.cos(a)}
        />
      );
    })}
    <g className="spin">
      <line className="frame" x1="150" y1="130" x2="150" y2="60" />
      <circle className="node" cx="150" cy="60" r="4" />
    </g>
    <g className="spin" style={{ animationDuration: '20s' }}>
      <line className="frame soft" x1="150" y1="130" x2="206" y2="130" />
    </g>
    <circle className="node" cx="150" cy="130" r="5" />
  </HeroArt>
);

/*
 * Interactive zigzag timeline — a serpentine copper trace connects each
 * milestone node. The path is measured from the real card positions, drawn
 * progressively as you scroll, and pulse dots travel along it.
 */

const TimelineWrap = styled.div`
  position: relative;
  max-width: 980px;
  margin: 0 auto;
`;

const PathSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;

  /* faint dotted twin painted above the cards — visible only where the
     solid trace is hidden underneath a card */
  &.overlay {
    z-index: 2;

    .trace-dotted {
      fill: none;
      stroke: rgba(242, 176, 94, 0.26);
      stroke-width: 1.5;
      stroke-dasharray: 2.5 8;
      stroke-linecap: round;
    }
  }

  .ghost {
    fill: none;
    stroke: var(--line-strong);
    stroke-width: 1.5;
    stroke-dasharray: 7 7;
  }

  .live {
    fill: none;
    stroke: url(#copperTrace);
    stroke-width: 2.5;
    stroke-linecap: round;
    filter: drop-shadow(0 0 6px rgba(224, 141, 60, 0.55));
  }

  .pulse {
    fill: var(--copper-bright);
    filter: drop-shadow(0 0 8px rgba(242, 176, 94, 0.9));
  }

  @media (prefers-reduced-motion: reduce) {
    .pulse {
      display: none;
    }
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 1fr;
  align-items: center;
  margin-bottom: clamp(4.5rem, 11vw, 8.5rem);

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 760px) {
    grid-template-columns: 64px 1fr;
    margin-bottom: 2rem;
  }
`;

const NodeCell = styled.div`
  grid-column: 2;
  display: flex;
  justify-content: center;

  @media (max-width: 760px) {
    grid-column: 1;
  }
`;

const Node = styled.button`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: var(--bg-elev);
  border: 1px solid ${(p) => (p.$active ? 'var(--copper)' : 'var(--line-strong)')};
  color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'var(--ink-faint)')};
  font-family: var(--font-mono);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  position: relative;
  z-index: 3;
  transition: all 0.3s var(--ease-out);
  box-shadow: ${(p) =>
    p.$active
      ? '0 0 0 6px rgba(224, 141, 60, 0.12), 0 0 24px rgba(224, 141, 60, 0.35)'
      : '0 0 0 6px rgba(236, 233, 225, 0.01)'};

  &:hover {
    border-color: var(--copper);
    color: var(--copper-bright);
    transform: scale(1.06);
  }

  @media (max-width: 760px) {
    width: 46px;
    height: 46px;
    font-size: 0.68rem;
  }
`;

const DateCard = styled.div`
  grid-column: ${(p) => (p.$side === 'left' ? 1 : 3)};
  grid-row: 1;
  position: relative;
  z-index: 1; /* above the solid trace, below the dotted overlay */
  background: var(--bg-card);
  border: 1px solid ${(p) => (p.$active ? 'rgba(224, 141, 60, 0.55)' : 'var(--line)')};
  border-radius: var(--radius);
  padding: 1.7rem 1.9rem;
  cursor: pointer;
  transition: transform 0.3s var(--ease-out), border-color 0.3s, box-shadow 0.3s;
  text-align: ${(p) => (p.$side === 'left' ? 'right' : 'left')};
  box-shadow: ${(p) => (p.$active ? '0 18px 44px -20px rgba(224, 141, 60, 0.35)' : 'none')};

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(224, 141, 60, 0.45);
  }

  .date {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--copper-bright);
    margin-bottom: 0.55rem;
  }

  h2 {
    font-size: 1.28rem;
    font-weight: 700;
    margin-bottom: 0.55rem;
  }

  p {
    color: var(--ink-dim);
    font-size: 0.93rem;
    line-height: 1.65;
    margin-bottom: 1rem;
  }

  @media (max-width: 760px) {
    grid-column: 2;
    text-align: left;
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.38rem 0.9rem;
  border-radius: 999px;
  color: ${(p) => (p.$status === 'deadline' ? 'var(--copper-bright)' : 'var(--plasma)')};
  border: 1px solid
    ${(p) => (p.$status === 'deadline' ? 'rgba(224, 141, 60, 0.4)' : 'rgba(95, 212, 194, 0.4)')};
  background: ${(p) =>
    p.$status === 'deadline' ? 'rgba(224, 141, 60, 0.08)' : 'rgba(95, 212, 194, 0.08)'};
`;

const ConferenceMark = styled.div`
  position: relative;
  overflow: hidden;
  margin: clamp(3.5rem, 8vw, 5.5rem) auto 0;
  max-width: 980px;
  text-align: center;
  border: 1px solid rgba(224, 141, 60, 0.4);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(236, 233, 225, 0.78) 0%, rgba(236, 233, 225, 0.62) 45%, rgba(236, 233, 225, 0.88) 100%),
    url('/assets/iitg-campus.jpg') center 38% / cover no-repeat;
  padding: clamp(2.4rem, 5vw, 3.4rem) 1.5rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(110% 150% at 50% -30%, rgba(224, 141, 60, 0.18), transparent 60%);
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  .label {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: var(--copper);
    margin-bottom: 0.8rem;
  }

  .date {
    font-family: var(--font-display);
    font-size: clamp(1.7rem, 4.5vw, 2.8rem);
    font-weight: 800;

    span {
      color: var(--copper);
    }
  }
`;

export default function KeyDates() {
  const wrapRef = useRef(null);
  const nodeRefs = useRef([]);
  const livePathRef = useRef(null);
  const [pathD, setPathD] = useState('');
  const [active, setActive] = useState(0);

  // Build a smooth serpentine path through the measured node centres
  const computePath = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const pts = nodeRefs.current
      .filter(Boolean)
      .map((node) => {
        const r = node.getBoundingClientRect();
        return {
          x: r.left + r.width / 2 - wrapRect.left,
          y: r.top + r.height / 2 - wrapRect.top,
        };
      });
    if (pts.length < 2) return;

    // widescreen map: arcs sweep almost the full container width,
    // alternating right → left like a route over terrain
    const isNarrow = wrapRect.width < 700;
    const amp = isNarrow ? 30 : Math.min(wrapRect.width * 0.46, wrapRect.width / 2 - 40);

    let d = `M ${pts[0].x} ${pts[0].y - 80} L ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const bulge = i % 2 ? amp : -amp;
      const lift = (b.y - a.y) * 0.08;
      // two control points pushed far to the side produce a near-circular sweep
      d += ` C ${a.x + bulge} ${a.y + lift}, ${b.x + bulge} ${b.y - lift}, ${b.x} ${b.y}`;
    }
    const last = pts[pts.length - 1];
    d += ` L ${last.x} ${last.y + 80}`;
    setPathD(d);
  };

  useLayoutEffect(() => {
    computePath();
    const onResize = () => computePath();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw the copper trace progressively with scroll
  useEffect(() => {
    const path = livePathRef.current;
    if (!path || !pathD) return undefined;
    const len = path.getTotalLength();
    path.style.strokeDasharray = `${len}`;
    path.style.strokeDashoffset = `${len}`;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      path.style.strokeDashoffset = '0';
      return undefined;
    }

    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        const viewport = window.innerHeight;
        const progress = Math.min(1, Math.max(0, (viewport * 0.82 - rect.top) / rect.height));
        path.style.strokeDashoffset = `${len * (1 - progress)}`;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [pathD]);

  return (
    <>
      <PageHero
        kicker="Key Dates"
        index="03"
        title="The road to June 2027."
        lead="Important deadlines and milestones for IEMENTech 2027. Exact dates will be announced shortly — follow the trace."
        art={DatesArt}
      >
        <div style={{ marginTop: '1.8rem' }}>
          <StatusPill color="copper" icon={<FiClock aria-hidden="true" />}>
            Timeline — to be announced
          </StatusPill>
        </div>
      </PageHero>

      <Section>
        <Container>
          <TimelineWrap ref={wrapRef}>
            <PathSvg aria-hidden="true">
              <defs>
                <linearGradient id="copperTrace" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f2b05e" />
                  <stop offset="55%" stopColor="#e08d3c" />
                  <stop offset="100%" stopColor="#b05e1e" />
                </linearGradient>
              </defs>
              {pathD && (
                <>
                  <path className="ghost" d={pathD} />
                  <path className="live" d={pathD} ref={livePathRef} />
                  {[0, 4, 8].map((delay, i) => (
                    <circle key={i} className="pulse" r={i === 0 ? 5 : 3.5}>
                      <animateMotion dur="12s" begin={`${delay}s`} repeatCount="indefinite" path={pathD} />
                    </circle>
                  ))}
                </>
              )}
            </PathSvg>

            {pathD && (
              <PathSvg className="overlay" aria-hidden="true">
                <path className="trace-dotted" d={pathD} />
              </PathSvg>
            )}

            {KEY_DATES.map((item, i) => {
              const side = i % 2 === 0 ? 'left' : 'right';
              return (
                <ItemRow key={item.title}>
                  <DateCard
                    $side={side}
                    $active={active === i}
                    onClick={() => setActive(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActive(i);
                      }
                    }}
                  >
                    <Reveal delay={i * 60}>
                      <p className="date">{item.date}</p>
                      <h2>{item.title}</h2>
                      <p>{item.description}</p>
                      <Badge $status={item.status}>
                        {item.status === 'deadline' ? 'Deadline' : 'Notification'}
                      </Badge>
                    </Reveal>
                  </DateCard>
                  <NodeCell>
                    <Node
                      ref={(el) => {
                        nodeRefs.current[i] = el;
                      }}
                      $active={active === i}
                      onClick={() => setActive(i)}
                      aria-label={`Milestone ${i + 1}: ${item.title}`}
                    >
                      /{String(i + 1).padStart(2, '0')}
                    </Node>
                  </NodeCell>
                </ItemRow>
              );
            })}
          </TimelineWrap>

          <Reveal>
            <ConferenceMark>
              <p className="label">Destination · Conference Days</p>
              <p className="date">
                {CONFERENCE.dateLabel} <span>·</span> {CONFERENCE.venueShort}
              </p>
            </ConferenceMark>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
